// Five Workspace Entry Points — shared capability registry (mirrors backend core/capabilities.py).
export const WS = {
  ROUT_ADMIN: 'workspace.rout_admin',
  ROUT_CLIENT: 'workspace.rout_client',
  TFS_ADMIN: 'workspace.tfs_admin',
  TFS_OPS: 'workspace.tfs_ops',
  FIELD: 'workspace.field',
  PAYROLL: 'workspace.payroll',
  CONTRACT_BUDGETS: 'workspace.contract_budgets',
  TFS_CRM: 'app.tfs_crm',
  TEAM: 'workspace.team',
};

export const CAP = {
  ADMIN_CONTROLS: 'admin.controls',
  CLIENT_ON_BEHALF: 'client.acting_on_behalf',
  FIELD_SUPERVISOR: 'field.supervisor',
  MD_VIEW: 'masterdata.view',
  MD_CUSTOMERS: 'masterdata.manage_customers',
  MD_LOCATIONS: 'masterdata.manage_locations',
  MD_REACTIVATE: 'masterdata.reactivate',
  PERSONNEL_VIEW: 'personnel.view',
  PERSONNEL_MANAGE: 'personnel.manage',
  PERSONNEL_REACTIVATE: 'personnel.reactivate',
  ASSIGNMENT_VIEW: 'assignment.view',
  ASSIGNMENT_MANAGE: 'assignment.manage',
  ASSIGNMENT_REACTIVATE: 'assignment.reactivate',
  PAYROLL_VIEW: 'payroll.view',
  PAYROLL_ENTER_ASSIGNED: 'payroll.enter_assigned',
  PAYROLL_PREPARE: 'payroll.prepare',
  PAYROLL_APPROVE: 'payroll.approve',
  PAYROLL_PROCESS: 'payroll.process',
  TWB_VIEW: 'tagwork_budgets.view',
  TWB_MANAGE: 'tagwork_budgets.manage',
  TWB_APPROVE: 'tagwork_budgets.approve',
  TWB_REPORT: 'tagwork_budgets.report',
};

// Company Master Data authority. Owner (Global Admin) and admin.controls always qualify;
// otherwise any specific master-data capability grants access to the section.
export const canViewMasterData = (user) =>
  user?.role === 'owner' ||
  hasCap(user, CAP.ADMIN_CONTROLS) ||
  [CAP.MD_VIEW, CAP.MD_CUSTOMERS, CAP.MD_LOCATIONS, CAP.MD_REACTIVATE].some((c) => hasCap(user, c));

export const canManageCustomers = (user) =>
  user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, CAP.MD_CUSTOMERS);

export const canManageLocations = (user) =>
  user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, CAP.MD_LOCATIONS);

// Write authorization used to gate mutating controls INSIDE the shared managers.
// Mirrors backend core/masterdata_auth.py exactly: existing owner/admin/manager
// roles keep management access, PLUS the delegated capability.
const _ADMIN_ROLES = ['owner', 'admin', 'manager'];
export const canWriteCustomers = (user) =>
  _ADMIN_ROLES.includes(user?.role) || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, CAP.MD_CUSTOMERS);
export const canWriteLocations = (user) =>
  _ADMIN_ROLES.includes(user?.role) || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, CAP.MD_LOCATIONS);
// Reactivation is stricter: an ordinary admin/manager may NOT reactivate.
export const canReactivateMaster = (user) =>
  user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, CAP.MD_REACTIVATE);

// ---- Personnel Master (Phase 2). Broad admin/manager do NOT auto-qualify. ----
export const isGlobalAdmin = (user) => user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS);

// Owner-only permanent purge — strictly the Owner Global Administrator. Delegated
// / General / Sub-Global administrators are NOT permitted to purge.
export const isOwner = (user) => user?.role === 'owner';

export const canViewPersonnel = (user) =>
  user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) ||
  [CAP.PERSONNEL_VIEW, CAP.PERSONNEL_MANAGE, CAP.PERSONNEL_REACTIVATE].some((c) => hasCap(user, c));
export const canManagePersonnel = (user) =>
  user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, CAP.PERSONNEL_MANAGE);
export const canReactivatePersonnel = (user) =>
  user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, CAP.PERSONNEL_REACTIVATE);

// ---- Personnel Assignments (Phase 3). Separate from Personnel identity. ----
export const canViewAssignments = (user) =>
  user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) ||
  [CAP.ASSIGNMENT_VIEW, CAP.ASSIGNMENT_MANAGE, CAP.ASSIGNMENT_REACTIVATE].some((c) => hasCap(user, c));
export const canManageAssignments = (user) =>
  user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, CAP.ASSIGNMENT_MANAGE);
export const canReactivateAssignments = (user) =>
  user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, CAP.ASSIGNMENT_REACTIVATE);

// Ordered entry points shown in the selector, grouped into three sections:
//   control      — internal control hubs (green outline)
//   applications — product apps in a 2x2 grid (per-card accent outline)
//   supporting   — supporting portals (green outline)
export const WORKSPACES = [
  // --- CONTROL MODULES ---
  { key: 'tfs_admin', cap: WS.TFS_ADMIN, route: '/tfs-admin', title: 'TFS Administration Hub', subtitle: 'Company, Users, Permissions & System Administration', family: 'TFS', section: 'control', accent: '#8FC93A' },
  { key: 'tfs_ops', cap: WS.TFS_OPS, route: '/tfs-ops', title: 'TFS Operations Hub', subtitle: 'Operational Oversight & Field Control', family: 'TFS', section: 'control', accent: '#8FC93A' },
  // --- APPLICATIONS (row of 4 vertical cards) ---
  { key: 'rout_admin', cap: WS.ROUT_ADMIN, route: '/rout-admin', title: 'ROUT', label: 'Work Orders', subtitle: 'Work Order Management & Control', family: 'ROUT', section: 'applications', img: '/app_rout_n.png', status: 'active', accent: '#EADFC6' },
  { key: 'tfs_crm', cap: WS.TFS_CRM, external: 'https://crm.tfsi-us.com/', title: 'CRM', label: 'CRM', subtitle: 'Sales Pipeline & Client Relationship Management', family: 'TFS', section: 'applications', img: '/app_reach_n.png', status: 'active', accent: '#7AB648', previewForOwner: true },
  { key: 'payroll', cap: WS.PAYROLL, route: '/payroll', title: 'Total Pay', label: 'Payroll', subtitle: 'Gross-Pay Preparation & Owner Approval', family: 'TFS', section: 'applications', img: '/app_total_pay_final_n.png', status: 'active', accent: '#1E3A8A' },
  { key: 'contract_budgets', cap: WS.CONTRACT_BUDGETS, route: '/contract-budgets', title: 'Tally', label: 'Budgets', subtitle: 'Account & Location Budget Management', family: 'TFS', section: 'applications', img: '/app_tally_final_n.png', status: 'active', accent: '#7C3AED', previewForOwner: true },
  // --- SUPPORTING ACCESS ---
  { key: 'rout_client', cap: WS.ROUT_CLIENT, route: '/client', title: 'ROUT Client Portal', label: 'Client Portal', subtitle: 'Client Requests & Service Visibility', family: 'ROUT', section: 'supporting', img: '/app_rout.png', accent: '#EADFC6' },
  { key: 'field', cap: WS.FIELD, route: '/field', title: 'TFS Field Portal', subtitle: 'Assigned Work & Field Reporting', family: 'TFS', section: 'supporting', accent: '#5FA718' },
  // --- COMPANY-WIDE: TEAM worker app (shell + access management available) ---
  { key: 'team', cap: WS.TEAM, route: '/team-hub', title: 'TEAM', label: 'T.E.A.M.', subtitle: 'Total Engagement, Access & Management', family: 'TEAM', section: 'company_wide', img: '/team_icon.png', status: 'active', accent: '#1C6FD6', previewForOwner: true },
];

// Informational future-application placeholders (no cap, no route, no data).
// Rendered on the App Hub welcome screen for full (admin/ops) users only.
// Each card matches its supplied icon by name and owns a dedicated identity color.
export const FUTURE_APPS = [
  // --- COMPANY-WIDE APPS ---
  { key: 'partners', title: 'Partners', label: 'Partners', subtitle: 'Partner Network & Collaboration', section: 'company_wide', img: '/partners_icon.png', status: 'coming', placeholder: true, accent: '#C8432F' },
  { key: 'calendar', title: 'Calendar', route: '/calendar', section: 'company_wide', img: '/app_calendar_n.png', status: 'active', accent: '#38BDF8' },
  { key: 'communication', title: 'Communication', section: 'company_wide', img: '/app_communication_clean.png', status: 'coming', placeholder: true, accent: '#14B8A6' },
  // --- OPERATIONAL APPS ---
  { key: 'incidents', title: 'Incidents', section: 'operational', img: '/app_incidents_n.png', status: 'coming', placeholder: true, accent: '#DC2626' },
  { key: 'inspections', title: 'Inspections', section: 'operational', img: '/app_inspections_n.png', status: 'coming', placeholder: true, accent: '#8B5CF6' },
  { key: 'inventory', title: 'Inventory', section: 'operational', img: '/app_inventory_n.png', status: 'coming', placeholder: true, accent: '#D946EF' },
  { key: 'pmp', title: 'PMP', section: 'operational', img: '/app_pmp_n.png', status: 'coming', placeholder: true, accent: '#94A3B8' },
  { key: 'training', title: 'Training', section: 'operational', img: '/app_training_n.png', status: 'coming', placeholder: true, accent: '#FACC15' },
  { key: 'vehicles', title: 'Vehicles', section: 'operational', img: '/app_vehicles_n.png', status: 'coming', placeholder: true, accent: '#2563EB' },
];

export const CLIENT_ROLES = ['client_facilities_director', 'client_location_requestor'];

export const hasCap = (user, cap) => Array.isArray(user?.capabilities) && user.capabilities.includes(cap);

export const authorizedWorkspaces = (user) =>
  WORKSPACES.filter((w) => hasCap(user, w.cap) || (w.previewForOwner && user?.role === 'owner'));

// Native (Capacitor) TEAM app: a dedicated single-purpose shell. Authorized
// users — including administrators — open TEAM directly; browser navigation is
// unaffected. Detection is runtime-only (Capacitor injects window.Capacitor).
export const isNativeApp = () => {
  try {
    return typeof window !== 'undefined' && !!window.Capacitor &&
      typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform();
  } catch (_) { return false; }
};
export const teamEnabled = (user) => isGlobalAdmin(user) || hasCap(user, WS.TEAM);

// Default landing: a single workspace routes straight in; multiple → the selector.
export const defaultRouteFor = (user) => {
  if (isNativeApp()) {
    if (!user) return '/team';
    if (teamEnabled(user)) return '/team-hub';
  }
  if (!user) return '/login';
  const ws = authorizedWorkspaces(user);
  if (ws.length === 0) return '/select-portal'; // shows a "no workspaces" state (avoids redirect loop)
  if (ws.length === 1 && ws[0].route) return ws[0].route; // external-only apps still show the selector
  return '/select-portal';
};

export const isClientUser = (user) => CLIENT_ROLES.includes(user?.role);

// ---- Budgets app (Tally) — section visibility. Contract Budgets and Tag-Work
// Budgets are independent grants; neither broadens the other. ----
export const canViewContractBudgets = (user) =>
  isGlobalAdmin(user) || hasCap(user, WS.CONTRACT_BUDGETS) ||
  ['contract_budgets.view', 'contract_budgets.manage', 'contract_budgets.approve', 'contract_budgets.report'].some((c) => hasCap(user, c));
export const canViewTagwork = (user) =>
  isGlobalAdmin(user) || [CAP.TWB_VIEW, CAP.TWB_MANAGE, CAP.TWB_APPROVE, CAP.TWB_REPORT].some((c) => hasCap(user, c));
export const canManageTagwork = (user) => isGlobalAdmin(user) || hasCap(user, CAP.TWB_MANAGE);
export const canApproveTagwork = (user) => isGlobalAdmin(user) || hasCap(user, CAP.TWB_APPROVE);
// Users who may reach the Budgets app shell (either sub-area).
export const canOpenBudgetsApp = (user) => canViewContractBudgets(user) || canViewTagwork(user);